import { Pressable, Text, View } from "react-native";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";

import { Icon } from "../Icon/Icon";
import { useArmedConfirm } from "./useArmedConfirm";
import { webTapFloor } from "../theme";

// <ConfirmRemoveButton> — the two-step ✕ remove idiom (tap → "Confirm this?"
// label → second tap executes, armed state auto-reverting via
// useArmedConfirm) lifted verbatim out of ProgramExerciseEditor and
// PlanExerciseEditor, its two byte-identical copies (CN-19,
// §CONSOLIDAMENTO). Callers keep owning the `removeBtn` / `removeBtnText`
// style objects (already a single shared PlanStyles definition) and pass
// them straight through, so the visual result is unchanged.
//
// Two a11y floors are fixed HERE rather than at the callsites, which is the
// point of having one component instead of two copies:
//   - hitSlop. The shared `removeBtn` is minHeight 40, under the 44pt floor,
//     and neither original carried a hitSlop. 2px a side closes it for both
//     on native. react-native-web's Pressable ignores hitSlop entirely
//     (DS-48), so web additionally gets `minWidth`/`minHeight: webTapFloor`
//     appended after the caller's style — a `null` array entry on native
//     (webTapFloor is undefined there) so the caller's 40 is never touched:
//     an object with an `undefined` value would clobber it via RN's
//     style-flattening, which is why this is a conditional array entry, not
//     a merged key. ⚠️ BOTH axes, not just height: PE-27's glyph callers sit
//     on a `minWidth: 28` box and lean on hitSlop for the rest, so flooring
//     height alone left the web target 28pt wide on the most destructive
//     controls in the matrix. `IconButton.tsx:260-261` already floors the
//     pair — this was the odd one out, not a new rule.
//   - the hint. At rest a screen reader announced "Remove exercise, button"
//     and, after activating, "Remove this exercise?, button", with nothing
//     saying a SECOND tap is what removes. The hint says it, and goes away
//     once armed (the label itself is the question at that point).
//     ⚠️ It is a floor OFFERED, not imposed: `confirmHint` is the caller's to
//     pass, and the glyph callers do not — their own spoken labels already
//     name what is lost. Don't read this as "every mount announces a hint".
//
// PE-27 — the GLYPH variant. A second hand-rolled copy of this idiom was
// living in PlacementMatrix (`ArmedRemove`) and a third inline in
// ProgramSpine, because the matrix needed a control whose rest state is a
// bare "−" inside a grid row rather than "✕ Remove day". Those are rendering
// differences over the SAME state machine, so they became props here instead
// of a third copy — which is what the fused grid would otherwise have shipped.
// Three knobs carry the whole difference:
//   - `showRestIcon={false}` — rest renders the label alone, no ✕ chrome.
//   - `spokenRestLabel` / `spokenConfirmLabel` — REQUIRED when the visible
//     text is a glyph: "−" announces nothing, so the button must be told what
//     to say. The labelled variant needs neither (its text IS the sentence).
//   - `armedStyle` / `armedTextStyle` — the matrix tints the whole box when
//     armed. ⛔ Never the only cue: the glyph itself goes − → ✕, so the armed
//     state survives without distinguishing tones.
export function ConfirmRemoveButton({
  onRemove,
  confirmLabel,
  removeLabel,
  confirmHint,
  style,
  textStyle,
  showRestIcon = true,
  spokenRestLabel,
  spokenConfirmLabel,
  armedStyle,
  armedTextStyle,
  hitSlop = 2,
}: {
  onRemove: () => void;
  /** Shown once armed; second tap on this label executes the removal. */
  confirmLabel: string;
  /** Shown at rest; first tap arms the confirm. */
  removeLabel: string;
  /** Announced at rest: says the first tap only arms the confirm. */
  confirmHint?: string;
  style: StyleProp<ViewStyle>;
  /** Also sources the ✕ icon's size + colour (same as the label). */
  textStyle: { color: string; fontSize: number; fontWeight?: TextStyle["fontWeight"] };
  /** False renders the rest label bare — for glyph controls inside a grid row. */
  showRestIcon?: boolean;
  /** Spoken at rest. Required when `removeLabel` is a glyph, which says nothing. */
  spokenRestLabel?: string;
  /** Spoken while armed — the WHOLE sentence, including what is lost. */
  spokenConfirmLabel?: string;
  /** Appended to `style` while armed. */
  armedStyle?: StyleProp<ViewStyle>;
  /** Appended to `textStyle` while armed. */
  armedTextStyle?: StyleProp<TextStyle>;
  /** Widens the touch box past the visual one. 8 for glyph controls. */
  hitSlop?: number;
}) {
  const [confirming, setConfirming] = useArmedConfirm();
  const spoken = confirming ? spokenConfirmLabel : spokenRestLabel;
  return (
    <Pressable
      accessibilityRole="button"
      {...(spoken ? { accessibilityLabel: spoken } : {})}
      {...(!confirming && confirmHint
        ? { accessibilityHint: confirmHint }
        : {})}
      hitSlop={hitSlop}
      onPress={() => {
        if (!confirming) {
          setConfirming(true);
          return;
        }
        setConfirming(false);
        onRemove();
      }}
      style={[
        style,
        confirming && armedStyle,
        webTapFloor !== undefined && {
          minWidth: webTapFloor,
          minHeight: webTapFloor,
        },
      ]}
    >
      {confirming ? (
        <Text style={[textStyle, armedTextStyle]}>{confirmLabel}</Text>
      ) : showRestIcon ? (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Icon name="close" size={textStyle.fontSize} color={textStyle.color} />
          <Text style={textStyle}>{removeLabel}</Text>
        </View>
      ) : (
        <Text style={textStyle}>{removeLabel}</Text>
      )}
    </Pressable>
  );
}
